// Librería de utilidades para Embedded Service (Salesforce Embedded Messaging)
// Proporciona funciones para cargar el script, inicializarlo y lanzar el chat.

// Aseguramos que este archivo sea un módulo para habilitar `declare global`

export {};

// Tipado mínimo del objeto global expuesto por el script de Embedded Messaging
interface EmbeddedServiceBootstrap {
	settings: {
		hideChatButtonOnLoad?: boolean;
		language?: string;
		chatButtonPosition?: string;
	};
	init: (
		orgId: string,
		deploymentName: string,
		url: string,
		options?: { scrt2URL?: string },
	) => void;
	utilAPI: {
		launchChat: () => void;
	};
}

declare global {
	interface Window {
		embeddedservice_bootstrap?: EmbeddedServiceBootstrap;
	}
}

export interface SfEmbeddedServiceConfig {
	key: string;
	urlOrigin: string;
	customConfig: {
		open: boolean;
		closedHeader: string;
		closedMsg: string;
		sfEmbeddedConfig: SfEmbeddedConfig;
		operatingHours: {
			openDays: Array<number>;
			openTime: {
				hour: number;
				minute: number;
			};
			closeTime: {
				hour: number;
				minute: number;
			};
			closedHeader: string;
			closedMsg: string;
		};
	};
}

interface SfEmbeddedConfig {
	orgId: string;
	deploymentName: string;
	url: string;
	scrt2URL?: string;
	language?: string;
	hideChatButtonOnLoad?: boolean;
	chatButtonPosition?: string;
}

// ---- API pública ----

// Comprueba si estamos en entorno navegador (evita romper SSR)
export function isBrowser(): boolean {
	return typeof window !== 'undefined' && typeof document !== 'undefined';
}

// Devuelve si el objeto global de Embedded Service está disponible
export function isEmbeddedServiceAvailable(): boolean {
	return (
		isBrowser() && typeof window.embeddedservice_bootstrap !== 'undefined'
	);
}

// Obtiene el objeto global tipado (o lanza error si no existe)
export function getEmbeddedService(): EmbeddedServiceBootstrap {
	if (!isEmbeddedServiceAvailable()) {
		throw new Error('embeddedservice_bootstrap no está disponible');
	}
	return window.embeddedservice_bootstrap!;
}

// Carga el script de Embedded Service una sola vez
export async function loadEmbeddedMessagingScript(
	src: string,
	opts: {
		id?: string; // id del tag script para reuso
		defer?: boolean;
		async?: boolean;
		crossOrigin?: string;
		integrity?: string;
	} = {},
): Promise<void> {
	if (!isBrowser()) return; // en SSR no hacemos nada

	const id = opts.id ?? 'embeddedservice-bootstrap-js';
	const existingById = document.getElementById(
		id,
	) as HTMLScriptElement | null;
	if (existingById && existingById.src === src) return; // ya cargado

	// Busca otro script con el mismo src por seguridad
	const existsSameSrc = Array.from(
		document.getElementsByTagName('script'),
	).some(s => s.src === src);
	if (existsSameSrc) return;

	await new Promise<void>((resolve, reject) => {
		const script = document.createElement('script');
		script.type = 'text/javascript';
		script.src = src;
		script.defer = Boolean(opts.defer);
		script.async = opts.async !== false; // por defecto async=true
		if (opts.crossOrigin) script.crossOrigin = opts.crossOrigin;
		if (opts.integrity) script.integrity = opts.integrity;
		script.id = id;

		script.onload = () => resolve();
		script.onerror = () =>
			reject(
				new Error('No se pudo cargar el script de Embedded Messaging'),
			);

		document.head.appendChild(script);
	});
}

// Inicializa el servicio embebido con opciones básicas
export function initEmbeddedMessaging(config: SfEmbeddedConfig): void {
	if (!isEmbeddedServiceAvailable()) {
		throw new Error(
			'No se puede inicializar: embeddedservice_bootstrap no está listo',
		);
	}

	const es = getEmbeddedService();
	if (typeof config.hideChatButtonOnLoad === 'boolean') {
		es.settings.hideChatButtonOnLoad = config.hideChatButtonOnLoad;
	}
	if (config.language) {
		es.settings.language = config.language;
	}

	// Si se pasa por config, lo aplicamos; no forzamos valor por defecto aquí
	if (config.chatButtonPosition) {
		es.settings.chatButtonPosition = config.chatButtonPosition;
	}

	es.init(config.orgId, config.deploymentName, config.url, {
		scrt2URL: config.scrt2URL,
	});
}

// Conveniencia: cambiar idioma dinámicamente
export function setEmbeddedMessagingLanguage(language: string): void {
	const es = getEmbeddedService();
	es.settings.language = language;
}

// Conveniencia: ocultar/mostrar botón del chat al cargar
export function setHideChatButtonOnLoad(hide: boolean): void {
	const es = getEmbeddedService();
	es.settings.hideChatButtonOnLoad = hide;
}

// Lanza la ventana de chat si está disponible
export function launchSfEmbeddedChat(): void {
	const es = getEmbeddedService();
	es.utilAPI.launchChat();
}

// ---- Inicialización de alto nivel (carga + init) ----

let embeddedInitPromise: Promise<void> | undefined;
let hasInitCalled = false; // evita múltiples init desde esta librería

// Asegura que el script esté cargado e inicializado una sola vez
export function ensureSfEmbeddedMessagingInitialized(
	sfEmbeddedServiceConfig?: SfEmbeddedServiceConfig,
): Promise<void> {
	if (!sfEmbeddedServiceConfig) {
		throw new Error(
			'No se puede inicializar: Falta sfEmbeddedServiceConfig',
		);
	}

	if (embeddedInitPromise) return embeddedInitPromise;
	if (!isBrowser()) {
		embeddedInitPromise = Promise.reject(
			new Error('Embedded Messaging no disponible fuera de navegador'),
		);
		return embeddedInitPromise;
	}

	// Si el host ya expuso el global, asumimos script cargado. Para evitar dobles inits,
	// no llamamos de nuevo a init aquí. Usaremos el bootstrap existente.
	if (isEmbeddedServiceAvailable()) {
		embeddedInitPromise = Promise.resolve();
		return embeddedInitPromise;
	}

	embeddedInitPromise = (async () => {
		await loadEmbeddedMessagingScript(sfEmbeddedServiceConfig.urlOrigin);
		// En onload el global debe estar disponible; ahora sí llamamos a init una sola vez
		if (!hasInitCalled) {
			hasInitCalled = true;
			initEmbeddedMessaging(
				sfEmbeddedServiceConfig.customConfig.sfEmbeddedConfig,
			);
		}
	})().catch(err => {
		// Permite reintentos en llamadas futuras si falló
		embeddedInitPromise = undefined;
		hasInitCalled = false;
		return Promise.reject(err);
	});

	return embeddedInitPromise;
}
